"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="205985" className="hidden md:flex gap-8">
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.filter(item => item.label !== "Gallery2").map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative py-2 text-sm font-default uppercase tracking-widest text-white font-medium transition-colors hover:text-primary group">
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-10 items-center">
        <div className="h-6 w-32 bg-muted/20 animate-pulse rounded" />
        <div className="h-6 w-32 bg-muted/20 animate-pulse rounded" />
        <div className="h-6 w-32 bg-muted/20 animate-pulse rounded" />
      </div>;
  }
  const sortedItems = [...menuItems].filter(item => item.label !== "Gallery").sort((a, b) => {
    const order = ["Full Detail", "Interior Detail", "Exterior Detail"];
    const aIdx = order.indexOf(a.label);
    const bIdx = order.indexOf(b.label);
    const aVal = aIdx === -1 ? 999 : aIdx;
    const bVal = bIdx === -1 ? 999 : bIdx;
    return aVal - bVal;
  });
  return <div className="flex flex-col gap-6 mt-10 items-center">
      {sortedItems.map((item, i) => <div key={item.id} data-index={i} className="w-full">
          <Link to={item.href || "#"} className="text-lg font-default uppercase tracking-widest text-white font-medium hover:text-white transition-colors text-center block w-full">
            {item.label}
          </Link>
        </div>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 dark bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-primary/20 shadow-lg shadow-black/40" : "bg-background border-b border-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between md:justify-center gap-8">
        {}
        <Link to="/" className="flex-shrink-0 z-10">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630148/img/brandforge_logo.png" alt="Ceeday Detailing" className="h-7 w-auto" />
        </Link>

        {}
        <MenuProvider menu_id="90">
          <DesktopMenuConsumer />
        </MenuProvider>

        {}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden text-foreground transition-all duration-300 ease-in-out hover:bg-transparent" aria-label="Open menu">
              <Menu className="size-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-background border-l border-primary/20 w-[300px] sm:w-[400px] data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right text-white">
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <MenuProvider menu_id="90">
              <MobileMenuConsumer />
            </MenuProvider>
          </SheetContent>
        </Sheet>
      </div>
    </section>;
}